import { useEffect, useState, useCallback, useRef } from "react";
import { Sun, RefreshCw, Sparkles, TrendingUp, TrendingDown, Minus, BookOpen, CheckCircle2, AlertTriangle, Target } from "lucide-react";
import { useCentralState } from "@/context/CentralStateContext";
import { computeCentralSnapshot, weekKeyOf, weekRangeLabel, diffSnapshots } from "@/lib/central";
import { iso } from "@/lib/wellness";
import { fmtMoney } from "@/lib/finance";
import { centralApi } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const Delta = ({ item }) => {
  const up = item.delta != null && item.delta > 0;
  const down = item.delta != null && item.delta < 0;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const color = up ? "var(--green)" : down ? "var(--danger)" : "var(--text-faint)";
  const fmt = (v) => (item.isMoney ? fmtMoney(v) : Math.round(v));
  const deltaText = item.delta == null ? "new" : `${item.delta > 0 ? "+" : ""}${item.isMoney ? fmtMoney(item.delta) : Math.round(item.delta)}`;
  return (
    <div className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-3" data-testid={`digest-delta-${item.key}`}>
      <div className="text-[11px] text-[var(--text-faint)] truncate">{item.key}</div>
      <div className="font-serif-display text-xl text-[var(--text)] tabular-nums">{fmt(item.cur)}</div>
      <div className="inline-flex items-center gap-1 text-[12px] mt-0.5" style={{ color }}>
        <Icon className="h-3.5 w-3.5" />{deltaText}{item.delta != null && <span className="text-[var(--text-faint)]"> vs last wk</span>}
      </div>
    </div>
  );
};

const List = ({ icon: Icon, tone, items, testid }) => (
  <ul className="space-y-2" data-testid={testid}>
    {items.map((t, i) => (
      <li key={i} className="flex items-start gap-2.5 text-[13px] text-[var(--text)]">
        <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: tone }} />{t}
      </li>
    ))}
  </ul>
);

export default function CentralDigest() {
  const { fin, life, wellness, loading } = useCentralState();
  const [state, setState] = useState({ loading: true, digest: null, snapshot: null, previousSnapshot: null });
  const [busy, setBusy] = useState(false);
  const autoTried = useRef(false);

  const weekKey = weekKeyOf(iso());

  const generate = useCallback(async () => {
    if (!fin) return;
    setBusy(true);
    try {
      const snapshot = computeCentralSnapshot(fin, life, wellness, iso());
      const res = await centralApi.generateDigest({ weekKey, snapshot });
      setState({ loading: false, digest: res.digest, snapshot: res.snapshot, previousSnapshot: res.previousSnapshot });
    } catch { toast.error("Couldn't prepare your weekly digest right now"); }
    finally { setBusy(false); }
  }, [fin, life, wellness, weekKey]);

  // Load cached digest for this week; auto-generate on first visit if none.
  useEffect(() => {
    if (loading || !fin) return;
    let active = true;
    (async () => {
      try {
        const res = await centralApi.getDigest(weekKey);
        if (!active) return;
        if (res.digest) {
          setState({ loading: false, digest: res.digest, snapshot: res.snapshot, previousSnapshot: res.previousSnapshot });
        } else if (!autoTried.current) {
          autoTried.current = true;
          await generate();
        } else {
          setState((s) => ({ ...s, loading: false }));
        }
      } catch {
        if (active) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, fin, weekKey]);

  if (loading || state.loading || (busy && !state.digest)) {
    return (
      <div>
        <PageHeader title="Weekly Digest" subtitle="Preparing your Sunday summary…" testid="central-digest-header" />
        <Skeleton className="h-40 rounded-[14px] mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-[12px]" />)}</div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 rounded-[14px]" />)}</div>
      </div>
    );
  }

  const d = state.digest;
  const deltas = d ? diffSnapshots(state.snapshot, state.previousSnapshot) : [];

  return (
    <div data-testid="central-digest">
      <PageHeader title="Weekly Digest" subtitle={`Your Sunday reset · Week of ${weekRangeLabel(weekKey)}`} testid="central-digest-header"
        actions={<Button variant="outline" size="sm" onClick={generate} disabled={busy} data-testid="regenerate-digest-button" className="border-[color:var(--border-subtle)] print:hidden"><RefreshCw className={cn("h-4 w-4 mr-1.5", busy && "animate-spin")} />Refresh</Button>} />

      {!d ? (
        <SectionCard testid="digest-empty">
          <div className="text-center py-6">
            <Sun className="h-8 w-8 text-[var(--gold)] mx-auto mb-3" />
            <h3 className="font-serif-display text-xl text-[var(--text)]">Your weekly digest is ready to prepare</h3>
            <p className="text-sm text-[var(--text-muted)] mt-1 max-w-md mx-auto">A warm, at-a-glance summary of your week across all three centers — with what to focus on next.</p>
            <Button onClick={generate} className="mt-4 bg-[var(--green)] text-[#f7fbf9]" data-testid="generate-digest-empty"><Sparkles className="h-4 w-4 mr-1.5" />Prepare digest</Button>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-4">
          <div className="rounded-[var(--radius-card)] border border-[color:rgba(198,139,60,0.35)] bg-[color:rgba(198,139,60,0.08)] p-6" data-testid="digest-headline-card">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]"><Sun className="h-3.5 w-3.5" />Sunday digest · Week of {weekRangeLabel(weekKey)}</div>
            {d.greeting && <p className="text-sm text-[var(--text-muted)] mt-2" data-testid="digest-greeting">{d.greeting}</p>}
            <h3 className="font-serif-display text-2xl text-[var(--text)] mt-1" data-testid="digest-headline">{d.headline}</h3>
            <p className="text-sm text-[var(--text)] mt-1.5 leading-relaxed" data-testid="digest-summary">{d.summary}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="digest-deltas">
            {deltas.map((item) => <Delta key={item.key} item={item} />)}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SectionCard title="Wins this week" testid="digest-wins-card">
              {d.wins.length ? <List icon={CheckCircle2} tone="var(--green)" items={d.wins} testid="digest-wins" /> : <p className="text-sm text-[var(--text-muted)]">A fresh week to build on.</p>}
            </SectionCard>
            <SectionCard title="Watch" testid="digest-watch-card">
              {d.watch.length ? <List icon={AlertTriangle} tone="var(--gold)" items={d.watch} testid="digest-watch" /> : <p className="text-sm text-[var(--text-muted)]">Nothing pressing — stay the course.</p>}
            </SectionCard>
            <SectionCard title="Focus for the coming week" testid="digest-focus-card">
              {d.focus.length ? <List icon={Target} tone="var(--green)" items={d.focus} testid="digest-focus" /> : <p className="text-sm text-[var(--text-muted)]">Rest well and begin gently.</p>}
            </SectionCard>
          </div>

          {d.scripture && (
            <SectionCard testid="digest-scripture-card">
              <div className="flex items-start gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-[10px] bg-[color:rgba(31,115,85,0.12)] text-[var(--green)] shrink-0"><BookOpen className="h-5 w-5" /></span>
                <div>
                  <p className="font-serif-display text-lg text-[var(--text)] leading-relaxed" data-testid="digest-scripture-text">“{d.scripture.text}”</p>
                  <p className="text-sm text-[var(--gold)] mt-1" data-testid="digest-scripture-ref">{d.scripture.reference}</p>
                </div>
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
}
